import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
  MaxLength,
} from 'class-validator';
import { Type } from 'class-transformer';
import { IsRgbaOrReference } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
} from '@builder4/shared/dto/craft/props/shared';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { RgbaDto } from '@builder4/shared/dto/styles';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
} from '@builder4/shared/types/animation';
import { IsEmbedUrl } from '@builder4/shared/dto/craft/validators/IsEmbedUrl';
import { IsSafeHtml } from '@builder4/shared/dto/craft/validators/IsSafeHtml';

const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
const SOURCE_TYPE_VALUES = ['url', 'html'] as const;
const FILL_SPACE_VALUES = ['yes', 'no'] as const;
export class EmbedBreakpointPropsDto {
  @IsOptional()
  @IsString()
  width?: string;

  @IsOptional()
  @IsString()
  height?: string;

  @IsOptional()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace?: 'yes' | 'no';

  @IsOptional()
  @IsString()
  flexBasis?: string;

  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;
}

export class EmbedPropsDto {
  // Source
  @IsDefined()
  @IsIn([...SOURCE_TYPE_VALUES])
  sourceType!: 'url' | 'html';

  @IsOptional()
  @IsString()
  @MaxLength(2048)
  @IsEmbedUrl()
  url?: string;

  @IsOptional()
  @IsString()
  @MaxLength(50000)
  @IsSafeHtml()
  htmlCode?: string;

  // Display
  @IsDefined()
  @IsNumber()
  @Min(0)
  @Max(1)
  opacity!: number;

  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minWidth!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minHeight!: number;

  @IsOptional()
  @IsBoolean()
  aspectRatioLock?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  aspectRatio?: number;

  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  margin!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  backgroundColor?: RgbaDto | string;

  @IsDefined()
  @IsIn([...POSITION_VALUES])
  position!: 'relative' | 'absolute' | 'fixed' | 'sticky';

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex!: number;

  @IsDefined()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace!: 'yes' | 'no';

  @IsDefined()
  @IsString()
  flexBasis!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  flexGrow!: number;

  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;

  @IsOptional()
  @ValidateNested()
  @Type(() => EmbedBreakpointPropsDto)
  tablet?: EmbedBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => EmbedBreakpointPropsDto)
  mobile?: EmbedBreakpointPropsDto;
}
